import { useGetFilterLabels } from '@app/components/data-table/hooks/use-get-filter-labels';
import PreviewSheet from '@app/components/preview/preview-sheet';
import { PreviewSheetBarChart } from '@app/components/preview/preview-sheet-bar-chart';
import PreviewSheetSection from '@app/components/preview/preview-sheet-chart-section';
import { useGetDirectGasEmissionsPreview } from '@shared/api/hooks/ccf/activities/direct-gas-emissions';
import { CompanyReportsActivitiesDirectGasEmissionsListSourcesItem } from '@shared/api/types';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatNumber } from '@shared/lib/utils';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useCCF } from '../../ccf-context';
import { reportSlugs } from '../../slugs';

export default function DirectGasPreviewSheet() {
  const [isOpen, setIsOpen] = useState(false);
  const { uuid: reportUuid, categories } = useCCF();

  const [sites] = useQueryState('site', parseAsArrayOf(parseAsString));
  const [gases] = useQueryState('gas', parseAsArrayOf(parseAsString));
  const [period] = useQueryState('period', parseAsArrayOf(parseAsString));
  const [sources] = useQueryState('source', parseAsArrayOf(parseAsString));

  const { flatLabels: tags } = useGetFilterLabels([
    'site',
    'gas',
    'period',
    'source',
  ]);

  const category = categories?.find(
    (item) => item.slug === reportSlugs.directGasEmissions
  );

  const { data: directGasEmissionsPreviewData, status } =
    useGetDirectGasEmissionsPreview({
      companyReportUuid: reportUuid!,
      params: {
        sites: sites || undefined,
        gases: gases || undefined,
        time_periods: period || undefined,
        sources: sources?.map(
          (source) =>
            source as CompanyReportsActivitiesDirectGasEmissionsListSourcesItem
        ),
      },
      enabled: isOpen,
    });

  const totalEmissionsTonnes =
    directGasEmissionsPreviewData?.totalEmissionsTonnes;

  const emissionsBySite = directGasEmissionsPreviewData?.emissionsBySite?.map(
    (site) => {
      return {
        site: site.siteName,
        emissions: site.totalSiteEmissionsTonnes,
        fill: 'var(--chart-3)',
      };
    }
  );

  const highestEmissionsActivities =
    directGasEmissionsPreviewData?.highestEmissionActivities?.map(
      (activity) => {
        return {
          gas: activity.name,
          emissions: activity.emissionsTonnes,
          fill: 'var(--chart-3)',
        };
      }
    );

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-1))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;
  return (
    <PreviewSheet
      title={`Scope ${category?.scope} - ${category?.name}`}
      status={status}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      metrics={[
        {
          title: 'Total emissions',
          value: formatNumber(totalEmissionsTonnes!),
          unit: 'tCO2e',
        },
      ]}
      tags={tags}
    >
      <PreviewSheetSection title="Emissions by site">
        <PreviewSheetBarChart
          nameKey="site"
          chartData={emissionsBySite}
          chartConfig={chartConfig}
        />
      </PreviewSheetSection>
      <PreviewSheetSection title="Highest emission gases">
        <PreviewSheetBarChart
          nameKey="gas"
          chartData={highestEmissionsActivities}
          chartConfig={chartConfig}
        />
      </PreviewSheetSection>
    </PreviewSheet>
  );
}
